'use client'

import * as React from 'react'
import { ChevronDownIcon } from 'lucide-react'
import { cn } from '@playbooks/ui/lib/utils'

export function MultilevelAccordionItem({
  children,
  renderTrigger,
  className,
  style,
  open = false,
  onChange,
  renderFooter,
  ref,
  ...props
}: React.PropsWithChildren<
  {
    renderTrigger: (args: { isOpen: boolean }) => React.ReactNode
    renderFooter?: (args: { isOpen: boolean }) => React.ReactNode
    open?: boolean
    className?: string
  } & React.HTMLAttributes<HTMLDivElement> &
    React.ComponentPropsWithRef<'div'>
>) {
  return (
    <div
      data-role="multilevel-accordion-item"
      ref={ref}
      style={style}
      className={cn('transition-colors duration-100', className)}
      {...props}
    >
      {renderTrigger({ isOpen: open })}

      <div
        className="grid transition-[grid-template-rows] duration-200 ease-in-out"
        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">{children}</div>
      </div>

      {renderFooter?.({ isOpen: open })}
    </div>
  )
}

export function MultilevelAccordionTrigger({
  isOpen,
  children,
  className,
  hideChevron = false,
  chevron,
  onClick,
}: React.PropsWithChildren<{
  isOpen: boolean
  className?: string
  hideChevron?: boolean
  chevron?: React.ReactNode
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
}>) {
  return (
    <div className={cn('flex items-center px-4 min-h-14 bg-background', className)} onClick={onClick}>
      {!hideChevron &&
        (chevron ? (
          chevron
        ) : (
          <span className={cn('translate-y-px shrink-0 transition-transform duration-200', isOpen && 'rotate-180')}>
            {chevron ?? <ChevronDownIcon size={16} />}
          </span>
        ))}

      {children}
    </div>
  )
}

export function MultilevelAccordionContent({
  children,
  className,
  onClick,
  ...props
}: React.PropsWithChildren<
  {
    className?: string
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  } & React.HTMLAttributes<HTMLDivElement>
>) {
  return (
    <div
      data-role="multilevel-accordion-content"
      className={cn('px-5 pb-5 text-sm', className)}
      onClick={onClick}
      {...props}
    >
      {children}
    </div>
  )
}
